import { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Form, Input, Select, message, Switch } from 'antd';

import { usePosts, useChallenges, useLearningPaths, useMetadata } from '../../../hooks/useCommunity';
import RichTextEditor from '../RichTextEditor';

const { TextArea } = Input;

const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const checkTheme = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(checkTheme);
    if (typeof document !== 'undefined') {
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    return () => observer.disconnect();
  }, []);
  
  return isDarkMode;
};

const MODAL_CLASSES = "[&_.ant-modal-content]:dark:!bg-[#161616] [&_.ant-modal-header]:dark:!bg-[#161616] [&_.ant-modal-header]:dark:!border-gray-800 [&_.ant-modal-title]:dark:!text-white [&_.ant-modal-title]:blog-title [&_.ant-modal-title]:text-2xl [&_.ant-modal-close]:dark:!text-gray-400 [&_.ant-modal-close:hover]:dark:!text-white [&_.ant-modal-footer]:dark:!bg-[#161616] [&_.ant-modal-footer]:dark:!border-gray-800 [&_.ant-modal-footer_.ant-btn-primary]:!bg-primary [&_.ant-modal-footer_.ant-btn-primary]:!border-primary [&_.ant-modal-footer_.ant-btn-primary:hover]:!bg-[#229bb8] [&_.ant-modal-footer_.ant-btn-default]:dark:!bg-[#0E0E0E] [&_.ant-modal-footer_.ant-btn-default]:dark:!border-gray-800 [&_.ant-modal-footer_.ant-btn-default]:dark:!text-gray-300";
const FORM_CLASSES = "[&_.ant-form-item-label>label]:dark:!text-gray-200 [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-input]:dark:!placeholder-gray-500 [&_.ant-select-selector]:dark:!bg-[#0E0E0E] [&_.ant-select-selector]:dark:!border-gray-800 [&_.ant-select-selection-item]:dark:!text-white [&_.ant-form-item-explain]:dark:!text-gray-400 [&_.ant-select-selection-placeholder]:dark:!text-gray-500 [&_.ant-input-textarea-show-count]:dark:!text-gray-500 [&_.ant-switch]:dark:!bg-gray-700";

// Post Edit Modal
export const PostEditModal = ({ visible, post, onClose, onUpdated }) => {
  const [form] = Form.useForm();
  const { updatePost } = usePosts({}, { skip: true });
  const { categories, tags } = useMetadata();
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [fileList, setFileList] = useState([]);
  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: category.id,
        label: category.name,
        slug: category.slug || '',
      })),
    [categories]
  );
  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        value: tag.id,
        label: tag.name,
        slug: tag.slug || '',
      })),
    [tags]
  );
  const filterByLabelOrSlug = useCallback((inputValue, option) => {
    if (!option) {
      return false;
    }
    const text = `${option.label ?? ''} ${option.slug ?? ''} ${option.value ?? ''}`;
    return text.toLowerCase().includes((inputValue || '').toLowerCase());
  }, []);

  useEffect(() => {
    if (!post) {
      form.resetFields();
      setContent('');
      return;
    }

    form.setFieldsValue({
      title: post.title,
      category: post.category?.id,
      tags: post.tags?.map(tag => tag.id),
      is_pinned: Boolean(post.is_pinned),
      is_solved: Boolean(post.is_solved)
    });
    setContent(post.content || '');
    if (post.cover_image_url) {
      setFileList([{
        uid: '-1',
        name: 'cover_image.jpg',
        status: 'done',
        url: post.cover_image_url
      }]);
    } else {
      setFileList([]);
    }
  }, [post, form]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      let payload = { ...values, content, post_type: post?.post_type };
      // simple validation to ensure content exists
      const text = (content || '').replace(/<[^>]*>/g, '').trim();
      if (!text) {
        message.error('Content is required');
        setLoading(false);
        return;
      }

      if (post?.post_type === 'blog') {
        const formData = new FormData();
        Object.keys(payload).forEach(key => {
          if (Array.isArray(payload[key])) {
            payload[key].forEach(val => formData.append(key, val));
          } else if (payload[key] !== undefined && payload[key] !== null) {
            formData.append(key, payload[key]);
          }
        });
        
        const currentFile = fileList[0];
        if (currentFile?.originFileObj) {
          formData.append('cover_image', currentFile.originFileObj);
        } else if (!currentFile && post?.cover_image_url) {
          formData.append('cover_image', ''); 
        }
        payload = formData;
      }

      const updatedPost = await updatePost(post.slug, payload);
      if (updatedPost?.is_approved) {
        message.success('Post updated successfully');
      } else {
        message.info('Post updated and sent for admin review');
      }
      onUpdated && onUpdated(updatedPost);
      onClose();
    } catch (err) {
      console.error('Error updating post:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Post"
      open={visible}
      onOk={() => form.submit()}
      onCancel={onClose}
      okText="Save Changes"
      cancelText="Cancel"
      okButtonProps={{ className: '!bg-primary !border-primary hover:!bg-primary/90', loading }}
      width={1100}
      centered
      destroyOnHidden
      forceRender
      className={MODAL_CLASSES}
    >
      <Form form={form} onFinish={handleSave} layout="vertical" className={FORM_CLASSES} autoComplete="off">
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="category" label="Category" className="mb-0">
            <Select
              placeholder="Select category"
              allowClear
              showSearch
              filterOption={filterByLabelOrSlug}
              options={categoryOptions}
            />
          </Form.Item>
          
          <Form.Item name="tags" label="Tags" className="mb-0">
            <Select
              mode="tags"
              placeholder="Select tags"
              allowClear
              showSearch
              filterOption={filterByLabelOrSlug}
              popupMatchSelectWidth={false}
              options={tagOptions}
            />
          </Form.Item>
        </div>

        {post?.post_type === 'blog' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Cover Image</label>
            {fileList.length === 0 ? (
              <label
                htmlFor="edit-cover-image-input"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-800 bg-white dark:bg-[#0E0E0E] hover:border-primary dark:hover:border-primary cursor-pointer transition-colors duration-150"
              >
                <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm text-gray-600 dark:text-gray-300">Choose image</span>
                <input
                  id="edit-cover-image-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFileList([{
                        uid: Date.now().toString(),
                        name: file.name,
                        size: file.size,
                        status: 'done',
                        originFileObj: file,
                        url: URL.createObjectURL(file),
                      }]);
                    }
                  }}
                />
              </label>
            ) : (
              <div className="inline-flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0E0E0E]">
                <img
                  src={fileList[0].url || (fileList[0].originFileObj ? URL.createObjectURL(fileList[0].originFileObj) : '')}
                  alt="Cover"
                  className="w-12 h-12 rounded object-cover flex-shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-[180px] sm:max-w-xs">{fileList[0].name}</div>
                  {fileList[0].size && <div className="text-xs text-gray-400 dark:text-gray-500">{(fileList[0].size / 1024).toFixed(0)} KB</div>}
                </div>
                <button
                  type="button"
                  onClick={() => setFileList([])}
                  className="ml-1 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  title="Remove image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="ant-form-item-label"><label className="ant-form-item-required">Content</label></div>
          <RichTextEditor 
            form={form}
            isDarkMode={isDarkMode}
            fieldName="content"
            value={content}
            onChange={setContent}
            placeholder="Write your content..."
            onSave={() => form.submit()}
          />
        </div>

        {post?.post_type === 'forum' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item name="is_pinned" label="Pinned to Top" valuePropName="checked" className="mb-0">
              <Switch />
            </Form.Item>
            <Form.Item name="is_solved" label="Marked as Solved" valuePropName="checked" className="mb-0">
              <Switch />
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
};

// Challenge Edit Modal
export const ChallengeEditModal = ({ visible, challenge, onClose }) => {
  const [form] = Form.useForm();
  const { updateChallenge } = useChallenges({}, { skip: true });
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const isDarkMode = useDarkMode();
  const { categories, tags, loading: metadataLoading } = useMetadata();
  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: String(category.id),
        label: category.name,
        slug: category.slug || '',
      })),
    [categories]
  );
  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        value: String(tag.id),
        label: tag.name,
        slug: tag.slug || '',
      })),
    [tags]
  );
  const filterByLabelOrSlug = useCallback((inputValue, option) => {
    if (!option) {
      return false;
    }
    const text = `${option.label ?? ''} ${option.slug ?? ''} ${option.value ?? ''}`;
    return text.toLowerCase().includes((inputValue || '').toLowerCase());
  }, []);

  useEffect(() => {
    if (!challenge) {
      form.resetFields();
      setDescription('');
      return;
    }

    const initialTags = Array.isArray(challenge.tags)
      ? challenge.tags.map((tag) =>
          tag?.id !== undefined ? String(tag.id) : tag?.slug || tag?.name
        )
      : [];

    form.setFieldsValue({
      title: challenge.title,
      difficulty: challenge.difficulty,
      category: challenge.category?.id ? String(challenge.category.id) : undefined,
      tags: initialTags,
      points: challenge.points,
      flag: challenge.flag,
    });
    setDescription(challenge.description || '');
  }, [challenge, form]);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        description,
        category: values.category || null,
        tags: (values.tags || []).filter((tag) => tag !== null && tag !== ''),
      };
      const text = (description || '').replace(/<[^>]*>/g, '').trim();
      if (!text) {
        message.error('Description is required');
        setLoading(false);
        return;
      }
      await updateChallenge(challenge.slug, payload);
      onClose();
    } catch (err) {
      console.error('Error updating challenge:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Challenge"
      open={visible}
      onOk={() => form.submit()}
      onCancel={onClose}
      okText="Save Changes"
      cancelText="Cancel"
      okButtonProps={{ className: '!bg-primary !border-primary hover:!bg-primary/90', loading }}
      width={1100}
      centered
      destroyOnHidden
      forceRender
      className={MODAL_CLASSES}
    >
      <Form form={form} onFinish={handleSave} layout="vertical" className={FORM_CLASSES} autoComplete="off">
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Form.Item name="difficulty" label="Difficulty" rules={[{ required: true }]} className="mb-0"> 
            <Select>
              <Select.Option value="easy">Easy</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="hard">Hard</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="category" label="Category" className="mb-0"> 
            <Select
              allowClear
              loading={metadataLoading}
              placeholder="Select category"
              showSearch
              filterOption={filterByLabelOrSlug}
              options={categoryOptions}
            />
          </Form.Item>
          
          <Form.Item name="points" label="Points" rules={[{ required: true }]} className="mb-0">
            <Input type="number" />
          </Form.Item>
        </div>

        <Form.Item name="tags" label="Tags"> 
          <Select
            mode="tags"
            allowClear
            loading={metadataLoading}
            placeholder="Select or add tags"
            showSearch
            popupMatchSelectWidth={false}
            filterOption={filterByLabelOrSlug}
            options={tagOptions}
          />
        </Form.Item>
        
        <Form.Item name="flag" label="Flag Format" rules={[{ required: true }]}>
          <Input className="font-mono" />
        </Form.Item>

        <div className="mb-4">
          <div className="ant-form-item-label"><label className="ant-form-item-required">Description</label></div>
          <RichTextEditor 
            form={form}
            isDarkMode={isDarkMode}
            fieldName="description"
            value={description}
            onChange={setDescription}
            placeholder="Write the challenge description..."
            onSave={() => form.submit()}
          />
        </div>
      </Form>
    </Modal>
  );
};